/**
 * ZIP без сжатия — ровно столько формата, сколько нужно архиву дел.
 *
 * **Почему свой, а не библиотека.** Архив на девять десятых состоит из PDF и JPEG,
 * а они уже сжаты: deflate поверх них даёт проценты и стоит секунд на телефоне.
 * Остаётся складывание файлов встык — это двести строк, и они не тянут в предзагрузку
 * оболочки ещё один пакет ради одного экрана.
 *
 * **Почему не `CompressionStream`.** Он появился в Safari 16.4, а нижняя граница
 * матрицы — iOS 16.x ([NFR.md](docs/NFR.md)). Архив, который на части устройств
 * не собирается, хуже архива без сжатия. Сжатые записи мы всё же **читаем**: их кладёт
 * любой архиватор, если юрист распакует и запакует архив на компьютере.
 *
 * **Границы.** Формат без ZIP64: одна запись и весь архив — до 4 ГБ, записей —
 * до 65 535. Обе границы проверяются и дают внятную ошибку, а не битый файл.
 */

const LOCAL_HEADER = 0x04034b50;
const CENTRAL_HEADER = 0x02014b50;
const END_OF_DIRECTORY = 0x06054b50;

const LOCAL_SIZE = 30;
const CENTRAL_SIZE = 46;
const END_SIZE = 22;

/** Предел без ZIP64 — и по размеру записи, и по смещению в архиве. */
const MAX_BYTES = 0xffffffff;
const MAX_ENTRIES = 0xffff;

/** Имена в UTF-8: без этого флага «Иванов.pdf» приезжает крякозябрами. */
const UTF8_NAMES = 1 << 11;

const STORED = 0;
const DEFLATED = 8;

/** Файл внутри архива. Путь — с прямыми слэшами, как требует формат. */
export interface ZipEntry {
  path: string;
  blob: Blob;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

/** Контрольная сумма записи. По ней при чтении видно, что архив доехал целым. */
export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;

  for (let index = 0; index < bytes.length; index += 1) {
    crc = (CRC_TABLE[(crc ^ (bytes[index] ?? 0)) & 0xff] ?? 0) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** Время в формате MS-DOS: две секунды точности и год от 1980-го — как в формате. */
function dosStamp(at: number): { time: number; date: number } {
  const moment = new Date(at);
  const year = Math.max(1980, moment.getFullYear());

  return {
    time:
      (moment.getHours() << 11) | (moment.getMinutes() << 5) | Math.floor(moment.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((moment.getMonth() + 1) << 5) | moment.getDate(),
  };
}

/**
 * Собирает архив.
 *
 * Содержимое читается по одной записи и сразу уходит в список кусков будущего `Blob`:
 * весь архив одним массивом в памяти не собирается — на телефоне это сотни мегабайт.
 */
export async function writeZip(entries: ZipEntry[], at: number = Date.now()): Promise<Blob> {
  if (entries.length > MAX_ENTRIES) {
    throw new Error(`В архив не помещается больше ${MAX_ENTRIES} файлов`);
  }

  const stamp = dosStamp(at);
  const parts: BlobPart[] = [];
  const directory: Uint8Array<ArrayBuffer>[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.path);
    const bytes = new Uint8Array(await entry.blob.arrayBuffer());

    if (bytes.length > MAX_BYTES) throw new Error(`Файл ${entry.path} больше 4 ГБ`);

    const sum = crc32(bytes);
    const local = new Uint8Array(LOCAL_SIZE + name.length);
    const head = new DataView(local.buffer);

    head.setUint32(0, LOCAL_HEADER, true);
    head.setUint16(4, 20, true); // версия, необходимая для распаковки, — 2.0
    head.setUint16(6, UTF8_NAMES, true);
    head.setUint16(8, STORED, true);
    head.setUint16(10, stamp.time, true);
    head.setUint16(12, stamp.date, true);
    head.setUint32(14, sum, true);
    head.setUint32(18, bytes.length, true);
    head.setUint32(22, bytes.length, true);
    head.setUint16(26, name.length, true);
    head.setUint16(28, 0, true); // дополнительного поля нет
    local.set(name, LOCAL_SIZE);

    const record = new Uint8Array(CENTRAL_SIZE + name.length);
    const item = new DataView(record.buffer);

    item.setUint32(0, CENTRAL_HEADER, true);
    item.setUint16(4, 20, true); // чем создан
    item.setUint16(6, 20, true); // чем распаковывается
    item.setUint16(8, UTF8_NAMES, true);
    item.setUint16(10, STORED, true);
    item.setUint16(12, stamp.time, true);
    item.setUint16(14, stamp.date, true);
    item.setUint32(16, sum, true);
    item.setUint32(20, bytes.length, true);
    item.setUint32(24, bytes.length, true);
    item.setUint16(28, name.length, true);
    item.setUint32(42, offset, true);
    record.set(name, CENTRAL_SIZE);

    parts.push(local, bytes);
    directory.push(record);
    offset += local.length + bytes.length;

    if (offset > MAX_BYTES) throw new Error('Архив больше 4 ГБ — выгрузите дела по частям');
  }

  const directorySize = directory.reduce((total, record) => total + record.length, 0);
  const end = new Uint8Array(END_SIZE);
  const tail = new DataView(end.buffer);

  tail.setUint32(0, END_OF_DIRECTORY, true);
  tail.setUint16(8, entries.length, true);
  tail.setUint16(10, entries.length, true);
  tail.setUint32(12, directorySize, true);
  tail.setUint32(16, offset, true);

  return new Blob([...parts, ...directory, end], { type: 'application/zip' });
}

/** Распаковывает сжатую запись — её кладёт архиватор на компьютере, но не мы. */
async function inflate(data: Blob): Promise<Uint8Array<ArrayBuffer>> {
  if (typeof DecompressionStream !== 'function') {
    throw new Error('Этот архив сжат, а браузер не умеет его распаковывать');
  }

  const stream = data.stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Читает архив: путь → содержимое.
 *
 * Разбор идёт от **центрального каталога** в хвосте, а не подряд от начала: только
 * он говорит, где кончается каждая запись, если её писали потоком. Контрольная сумма
 * сверяется у каждой записи — иначе битый архив тихо развернётся в битые документы.
 */
export async function readZip(archive: Blob): Promise<Map<string, Blob>> {
  if (archive.size < END_SIZE) throw new Error('Это не архив: файл слишком мал');

  const tailSize = Math.min(archive.size, MAX_ENTRIES + END_SIZE);
  const tail = new Uint8Array(await archive.slice(archive.size - tailSize).arrayBuffer());
  const tailView = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);

  let end = -1;
  for (let index = tail.length - END_SIZE; index >= 0; index -= 1) {
    if (tailView.getUint32(index, true) === END_OF_DIRECTORY) {
      end = index;
      break;
    }
  }
  if (end < 0) throw new Error('Это не ZIP-архив');

  const count = tailView.getUint16(end + 10, true);
  const directorySize = tailView.getUint32(end + 12, true);
  const directoryAt = tailView.getUint32(end + 16, true);

  const directory = new Uint8Array(
    await archive.slice(directoryAt, directoryAt + directorySize).arrayBuffer(),
  );
  const view = new DataView(directory.buffer, directory.byteOffset, directory.byteLength);

  const files = new Map<string, Blob>();
  let cursor = 0;

  for (let index = 0; index < count; index += 1) {
    if (cursor + CENTRAL_SIZE > directory.length) {
      throw new Error('Архив повреждён: обрыв каталога');
    }
    if (view.getUint32(cursor, true) !== CENTRAL_HEADER) {
      throw new Error('Архив повреждён: запись без заголовка');
    }

    const method = view.getUint16(cursor + 10, true);
    const sum = view.getUint32(cursor + 16, true);
    const packedSize = view.getUint32(cursor + 20, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localAt = view.getUint32(cursor + 42, true);

    const name = directory.slice(cursor + CENTRAL_SIZE, cursor + CENTRAL_SIZE + nameLength);
    const path = decoder.decode(name);
    cursor += CENTRAL_SIZE + nameLength + extraLength + commentLength;

    // Папки лежат в архиве отдельными пустыми записями. Своего содержимого у них нет.
    if (path.endsWith('/')) continue;

    /*
     * Длины имени и дополнительного поля берутся из **локального** заголовка.
     *
     * В центральном каталоге они бывают другими: архиваторы кладут туда свои поля,
     * и данные записи начались бы не там, где мы их ждём.
     */
    const localBytes = new Uint8Array(
      await archive.slice(localAt, localAt + LOCAL_SIZE).arrayBuffer(),
    );
    const local = new DataView(localBytes.buffer, localBytes.byteOffset, localBytes.byteLength);
    if (local.getUint32(0, true) !== LOCAL_HEADER) {
      throw new Error(`Архив повреждён: не найден файл ${path}`);
    }

    const dataAt = localAt + LOCAL_SIZE + local.getUint16(26, true) + local.getUint16(28, true);
    const packed = archive.slice(dataAt, dataAt + packedSize);

    let bytes: Uint8Array<ArrayBuffer>;
    if (method === STORED) bytes = new Uint8Array(await packed.arrayBuffer());
    else if (method === DEFLATED) bytes = await inflate(packed);
    else throw new Error(`Файл ${path} упакован неизвестным способом`);

    if (crc32(bytes) !== sum) throw new Error(`Файл ${path} в архиве повреждён`);

    files.set(path, new Blob([bytes]));
  }

  return files;
}
